import React from 'react';
import { motion } from 'framer-motion';
import { plansData } from '../data/plansData';
import { FaCheckCircle, FaStar, FaFire, FaArrowRight, FaWhatsapp, FaShieldAlt, FaGift, FaUsers } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const Packages = () => {
  const transition = { type: 'spring', duration: 2.5 };

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.12, duration: 0.6 } }),
  };

  return (
    <div className="px-4 md:px-12 py-12 text-white overflow-hidden max-w-7xl mx-auto flex flex-col gap-16 min-h-screen">
      {/* ── 1. HEADER SECTION ── */}
      <motion.div
        className="text-center max-w-3xl mx-auto"
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={transition}
      >
        <span className="inline-block bg-red-600/15 border border-red-500/30 text-red-500 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4 shadow-lg">
          Transparent Pricing • 3 Units in Coimbatore
        </span>
        <div className="text-3xl sm:text-5xl md:text-6xl font-black uppercase tracking-tight mb-4 leading-none">
          <span className="text-transparent [-webkit-text-stroke:_1.5px_white]">MEMBERSHIP </span>
          <span className="text-red-500">PACKAGES</span>
        </div>
        <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
          No hidden fees or lock-ins. Select the transformation plan that fits your ambition. Access world-class equipment and certified coaching across Chettipalayam JJ Nagar, Pappampatti Pirivu, and Peedampalli.
        </p>
      </motion.div>

      {/* ── 2. PRICING CARDS GRID WITH HERO-STYLE ANIMATIONS ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
        {plansData.map((plan, i) => (
          <motion.div
            key={plan.id}
            className={`relative rounded-3xl p-8 backdrop-blur-2xl flex flex-col justify-between transition-all duration-500 shadow-2xl border ${
              plan.highlight
                ? 'bg-gradient-to-b from-[#1f222e]/98 via-[#181a24]/98 to-[#12141c]/98 border-red-500/80 shadow-[0_0_40px_rgba(229,27,36,0.25)] lg:-translate-y-4'
                : 'bg-[#161820]/90 border-white/10 hover:border-red-500/40'
            }`}
            custom={i}
            initial="hidden"
            whileInView="visible"
            variants={fadeUp}
            viewport={{ once: true }}
            whileHover={{ y: plan.highlight ? -18 : -10 }}
          >
            {/* Top Accent Strip */}
            <div
              className={`absolute top-0 left-0 w-full h-1.5 rounded-t-3xl ${
                plan.highlight
                  ? 'bg-gradient-to-r from-red-600 via-rose-500 to-red-700'
                  : 'bg-white/10'
              }`}
            />

            {/* Popular Pill Badge */}
            {plan.highlight && (
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-red-600 to-red-700 text-white px-5 py-1 rounded-full text-[11px] font-black uppercase tracking-widest shadow-xl shadow-red-600/40 border border-white/20 flex items-center gap-1.5">
                <FaFire className="text-white text-xs" /> MOST POPULAR
              </div>
            )}

            <div>
              {/* Plan Header */}
              <div className="flex items-center justify-between gap-2 mb-3">
                <span className="text-xs font-black uppercase text-red-500 tracking-wider">
                  {plan.badge}
                </span>
                {plan.highlight && (
                  <span className="flex text-amber-400 text-xs gap-0.5">
                    {[...Array(5)].map((_, idx) => (
                      <FaStar key={idx} />
                    ))}
                  </span>
                )}
              </div>

              <h3 className="text-2xl font-black uppercase tracking-wide text-white mb-2">
                {plan.name}
              </h3>

              <p className="text-gray-400 text-xs leading-relaxed mb-6">
                {plan.description}
              </p>

              {/* Price Display */}
              <div className="flex items-baseline gap-1 my-4 bg-white/5 p-4 rounded-2xl border border-white/5">
                <span className="text-2xl font-extrabold text-red-500">₹</span>
                <span className="text-4xl sm:text-5xl font-black text-white tracking-tight">{plan.price}</span>
                <span className="text-xs text-gray-400 font-semibold ml-1">{plan.period}</span>
              </div>

              <div className="h-px bg-white/10 w-full my-6" />

              {/* Features List */}
              <div className="flex flex-col gap-3.5 mb-8">
                <span className="text-[11px] font-bold uppercase tracking-widest text-gray-400">
                  Included Privileges:
                </span>
                {plan.features.map((feature, j) => (
                  <div className="flex items-start gap-3 text-xs sm:text-sm text-gray-200" key={j}>
                    <FaCheckCircle className="text-red-500 text-sm flex-shrink-0 mt-0.5" />
                    <span className="leading-snug">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-3 pt-4 border-t border-white/10">
              <Link
                to="/contact"
                className={`w-full font-black py-4 rounded-xl transition-all cursor-pointer text-xs uppercase tracking-wider flex items-center justify-center gap-2 shadow-lg ${
                  plan.highlight
                    ? 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white shadow-red-600/30 hover:scale-[1.02]'
                    : 'bg-white/10 hover:bg-white/20 text-white border border-white/15'
                }`}
              >
                <span>Start Your Fitness Journey</span>
                <FaArrowRight className="text-xs" />
              </Link>

              <a
                href={`https://wa.me/917339504861?text=Hi%20I'm%20interested%20in%20booking%20a%20fitness%20consultation%20for%20the%20${encodeURIComponent(plan.name)}%20plan%20at%20Idol%20Fitness%20Zone!`}
                target="_blank"
                rel="noreferrer"
                className="w-full text-center text-[11px] text-gray-300 hover:text-white flex items-center justify-center gap-1.5 transition-colors py-1"
              >
                <FaWhatsapp className="text-green-400" /> Book a Fitness Consultation
              </a>
            </div>
          </motion.div>
        ))}
      </div>

      {/* ── 3. VALUE-ADD PERKS STRIP ── */}
      <motion.div
        className="grid grid-cols-1 md:grid-cols-3 gap-6"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={transition}
        viewport={{ once: true }}
      >
        <div className="bg-[#161820]/90 p-6 rounded-3xl border border-white/10 hover:border-red-500/40 transition-colors shadow-xl">
          <div className="w-12 h-12 rounded-2xl bg-red-600/20 text-red-500 flex items-center justify-center text-xl mb-4">
            <FaGift />
          </div>
          <h4 className="text-lg font-bold text-white mb-1">1-Day Free Trial</h4>
          <p className="text-xs text-gray-300 leading-relaxed">
            Experience our Olympic equipment, lockers, and coaches before you decide. 100% free with no commitment.
          </p>
        </div>

        <div className="bg-[#161820]/90 p-6 rounded-3xl border border-white/10 hover:border-red-500/40 transition-colors shadow-xl">
          <div className="w-12 h-12 rounded-2xl bg-red-600/20 text-red-500 flex items-center justify-center text-xl mb-4">
            <FaShieldAlt />
          </div>
          <h4 className="text-lg font-bold text-white mb-1">Pause & Freeze Policy</h4>
          <p className="text-xs text-gray-300 leading-relaxed">
            Travelling for work or exams? Freeze your membership for up to 30 days without losing any active days.
          </p>
        </div>

        <div className="bg-[#161820]/90 p-6 rounded-3xl border border-white/10 hover:border-red-500/40 transition-colors shadow-xl">
          <div className="w-12 h-12 rounded-2xl bg-red-600/20 text-red-500 flex items-center justify-center text-xl mb-4">
            <FaUsers />
          </div>
          <h4 className="text-lg font-bold text-white mb-1">Group & Student Discount</h4>
          <p className="text-xs text-gray-300 leading-relaxed">
            Join with workout buddies or college friends (groups of 2+) and unlock exclusive student and group savings.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default Packages;
